import { gql } from "@apollo/client";

// ============================================================================
// LinkedIn Connection Status Types
// ============================================================================

export interface LinkedInConnectionStatus {
  isConnected: boolean;
  adAccountId?: string;
  adAccountName?: string;
  connectionStatus?: string;
  connectedAt?: string;
  expiresAt?: string;
  scopes?: string[];
}

export interface LinkedInOAuthResult {
  success: boolean;
  authUrl?: string;
  state?: string;
  error?: string;
}

export interface LinkedInDisconnectResult {
  success: boolean;
  error?: string;
}

// ============================================================================
// GraphQL Queries
// ============================================================================

export const GET_LINKEDIN_OAUTH_STATUS = gql`
  query GetLinkedInOAuthStatus {
    getLinkedInOAuthStatus {
      isConnected
      adAccountId
      adAccountName
      connectionStatus
      connectedAt
      expiresAt
      scopes
    }
  }
`;

export const LIST_LINKEDIN_AD_ACCOUNTS = gql`
  query ListLinkedInAdAccounts {
    listLinkedInAdAccounts {
      id
      name
      type
      status
      currency
    }
  }
`;

export const GET_LINKEDIN_CONVERSION_CONFIG = gql`
  query GetLinkedInConversionConfig($conversationId: ID!) {
    getLinkedInConversionConfig(conversationId: $conversationId) {
      id
      tenantId
      conversationId
      conversionUrn
      conversionName
      enabled
      triggerOn
      conversionValue
      currency
      createdAt
      updatedAt
    }
  }
`;

export const LIST_LINKEDIN_CONVERSION_CONFIGS = gql`
  query ListLinkedInConversionConfigs {
    listLinkedInConversionConfigs {
      id
      tenantId
      conversationId
      conversionUrn
      conversionName
      enabled
      triggerOn
      createdAt
      updatedAt
    }
  }
`;

export const GET_LINKEDIN_CONVERSION_STATS = gql`
  query GetLinkedInConversionStats($conversationId: ID!) {
    getLinkedInConversionStats(conversationId: $conversationId) {
      conversationId
      totalEvents
      successfulEvents
      failedEvents
      lastEventAt
      conversionRate
    }
  }
`;

// ============================================================================
// GraphQL Mutations
// ============================================================================

export const INITIATE_LINKEDIN_OAUTH = gql`
  mutation InitiateLinkedInOAuth($input: LinkedInOAuthInitiateInput!) {
    initiateLinkedInOAuth(input: $input) {
      success
      authUrl
      state
      error
    }
  }
`;

export const COMPLETE_LINKEDIN_OAUTH = gql`
  mutation CompleteLinkedInOAuth($input: LinkedInOAuthCallbackInput!) {
    completeLinkedInOAuth(input: $input) {
      success
      adAccountId
      adAccountName
      error
    }
  }
`;

export const UPDATE_LINKEDIN_AD_ACCOUNT = gql`
  mutation UpdateLinkedInAdAccount($input: UpdateLinkedInAdAccountInput!) {
    updateLinkedInAdAccount(input: $input)
  }
`;

export const DISCONNECT_LINKEDIN_ACCOUNT = gql`
  mutation DisconnectLinkedInAccount {
    disconnectLinkedInAccount
  }
`;

export const CREATE_LINKEDIN_CONVERSION_CONFIG = gql`
  mutation CreateLinkedInConversionConfig($input: LinkedInConversionConfigInput!) {
    createLinkedInConversionConfig(input: $input) {
      id
      tenantId
      conversationId
      conversionUrn
      conversionName
      enabled
      triggerOn
      conversionValue
      currency
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_LINKEDIN_CONVERSION_CONFIG = gql`
  mutation UpdateLinkedInConversionConfig($input: UpdateLinkedInConversionConfigInput!) {
    updateLinkedInConversionConfig(input: $input) {
      id
      tenantId
      conversationId
      conversionUrn
      conversionName
      enabled
      triggerOn
      conversionValue
      currency
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_LINKEDIN_CONVERSION_CONFIG = gql`
  mutation DeleteLinkedInConversionConfig($conversationId: ID!) {
    deleteLinkedInConversionConfig(conversationId: $conversationId)
  }
`;

// ============================================================================
// Response Types
// ============================================================================

export interface LinkedInConversionConfig {
  id: string;
  tenantId: string;
  conversationId: string;
  conversionUrn: string;
  conversionName: string;
  enabled: boolean;
  triggerOn: string;
  conversionValue?: number;
  currency?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LinkedInConversionStats {
  conversationId: string;
  totalEvents: number;
  successfulEvents: number;
  failedEvents: number;
  lastEventAt?: string;
  conversionRate: number;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Open LinkedIn OAuth popup and handle the flow
 */
export function openLinkedInOAuthPopup(authUrl: string): Promise<boolean> {
  return new Promise((resolve) => {
    console.log("Opening LinkedIn OAuth popup:", authUrl);

    const popup = window.open(
      authUrl,
      "linkedin-oauth",
      "width=600,height=700,scrollbars=yes,resizable=yes,location=yes"
    );

    if (!popup) {
      console.error("Failed to open popup - popup blocker may be active");
      resolve(false);
      return;
    }

    let resolved = false;

    // Listen for messages from the popup (for OAuth callback)
    const messageListener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === "LINKEDIN_OAUTH_SUCCESS") {
        console.log("Received OAuth success message");
        cleanup();
        resolved = true;
        resolve(true);
      } else if (event.data.type === "LINKEDIN_OAUTH_ERROR") {
        console.error("Received OAuth error message:", event.data.error);
        cleanup();
        resolved = true;
        resolve(false);
      }
    };

    window.addEventListener("message", messageListener);

    // Poll for popup closure as fallback
    const pollTimer = setInterval(() => {
      if (popup.closed) {
        console.log("Popup closed by user");
        cleanup();
        if (!resolved) {
          resolved = true;
          resolve(true); // Assume success if popup was closed normally
        }
      }
    }, 1000);

    // Cleanup function
    const cleanup = () => {
      clearInterval(pollTimer);
      window.removeEventListener("message", messageListener);
      if (!popup.closed) {
        popup.close();
      }
    };

    // Timeout after 5 minutes
    setTimeout(() => {
      if (!resolved) {
        console.log("OAuth popup timed out");
        cleanup();
        resolved = true;
        resolve(false);
      }
    }, 300000);
  });
}

/**
 * Format date for display
 */
export function formatLinkedInDate(dateString?: string): string {
  if (!dateString) return "Never";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Validate LinkedIn conversion URN format
 */
export function isValidConversionUrn(urn: string): boolean {
  return /^urn:lla:llaPartnerConversion:\d+$/.test(urn);
}

